import { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Lock, ArrowLeft, CheckCircle, XCircle, Eye, EyeOff } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_4935773c-183d-4ff8-bc9d-3030c509887e/artifacts/mknxgjj1_identra_logo_3d_clean_hd.jpg";

const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    const verifyToken = async () => {
      if (!token) {
        setVerifying(false);
        setTokenValid(false);
        return;
      }

      try {
        const response = await axios.get(`${API_URL}/api/auth/verify-reset-token?token=${token}`);
        setTokenValid(response.data.valid);
      } catch (error) {
        setTokenValid(false);
      } finally {
        setVerifying(false);
      }
    };

    verifyToken();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!password || !confirmPassword) {
      toast.error('Please fill in all fields');
      return;
    }

    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/auth/reset-password`, {
        token,
        new_password: password
      });
      setSuccess(true);
      toast.success('Password reset successful!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  // Loading state
  if (verifying) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="loading-spinner w-8 h-8 mx-auto mb-4" />
          <p className="text-muted-foreground">Verifying reset link...</p>
        </div>
      </div>
    );
  }

  // Invalid token state
  if (!tokenValid && !success) {
    return (
      <div className="auth-container">
        <div className="auth-form-side">
          <div className="max-w-md mx-auto w-full">
            <Link to="/" className="flex items-center gap-3 mb-12">
              <img src={LOGO_URL} alt="Identra" className="h-12 w-auto" />
            </Link>

            <div className="text-center">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <XCircle className="w-8 h-8 text-red-600" />
              </div>
              <h1 className="text-3xl font-bold tracking-tight mb-3">Invalid or expired link</h1>
              <p className="text-muted-foreground text-lg mb-8">
                This password reset link is no longer valid. It may have expired or already been used.
              </p>
              
              <div className="space-y-4">
                <Link to="/forgot-password">
                  <Button 
                    className="w-full h-12 bg-[#16335E] hover:bg-[#0A1A34]"
                    data-testid="request-new-link-btn"
                  >
                    Request a new link
                  </Button>
                </Link>
                <Link to="/login">
                  <Button 
                    variant="ghost"
                    className="w-full h-12"
                    data-testid="back-to-login-btn"
                  >
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Back to login
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>

        <div className="auth-visual-identra">
          <div className="relative z-10 text-center text-white">
            <XCircle className="w-20 h-20 mx-auto mb-6 opacity-80" />
            <h2 className="text-2xl font-bold mb-4">Link Expired</h2>
            <p className="text-white/80 max-w-sm mx-auto">
              Password reset links are valid for 1 hour for security reasons.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Success state
  if (success) {
    return (
      <div className="auth-container">
        <div className="auth-form-side">
          <div className="max-w-md mx-auto w-full">
            <Link to="/" className="flex items-center gap-3 mb-12">
              <img src={LOGO_URL} alt="Identra" className="h-12 w-auto" />
            </Link>

            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <h1 className="text-3xl font-bold tracking-tight mb-3">Password reset!</h1>
              <p className="text-muted-foreground text-lg mb-8">
                Your password has been successfully reset. You can now login with your new password.
              </p>
              
              <Link to="/login">
                <Button 
                  className="w-full h-12 bg-[#16335E] hover:bg-[#0A1A34]"
                  data-testid="go-to-login-btn"
                >
                  Go to login
                </Button>
              </Link>
            </div>
          </div>
        </div>

        <div className="auth-visual-identra">
          <div className="relative z-10 text-center text-white">
            <CheckCircle className="w-20 h-20 mx-auto mb-6 opacity-80" />
            <h2 className="text-2xl font-bold mb-4">All Done!</h2>
            <p className="text-white/80 max-w-sm mx-auto">
              Your account is now secure with your new password.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Reset password form
  return (
    <div className="auth-container">
      <div className="auth-form-side">
        <div className="max-w-md mx-auto w-full">
          <Link to="/" className="flex items-center gap-3 mb-12">
            <img src={LOGO_URL} alt="Identra" className="h-12 w-auto" />
          </Link>

          <div className="mb-8">
            <h1 className="text-4xl font-bold tracking-tight mb-3">Set new password</h1>
            <p className="text-muted-foreground text-lg">
              Your new password must be at least 6 characters.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="password" className="text-sm font-medium">
                New password
              </Label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="Enter new password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-12 pr-12 h-12 text-base"
                  data-testid="reset-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword" className="text-sm font-medium">
                Confirm password
              </Label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <Input
                  id="confirmPassword"
                  type={showConfirmPassword ? "text" : "password"}
                  placeholder="Confirm new password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="pl-12 pr-12 h-12 text-base"
                  data-testid="reset-confirm-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <Button 
              type="submit" 
              className="w-full h-12 text-base font-semibold bg-[#16335E] hover:bg-[#0A1A34]"
              disabled={loading}
              data-testid="reset-submit-btn"
            >
              {loading ? (
                <div className="loading-spinner w-5 h-5" />
              ) : (
                <>
                  Reset password
                  <CheckCircle className="w-5 h-5 ml-2" />
                </>
              )}
            </Button>
          </form>

          <p className="mt-8 text-center">
            <Link 
              to="/login" 
              className="text-muted-foreground hover:text-foreground inline-flex items-center"
              data-testid="back-link"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to login
            </Link>
          </p>
        </div>
      </div>

      <div className="auth-visual-identra">
        <div className="relative z-10 text-center text-white">
          <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <Lock className="w-10 h-10" />
          </div>
          <h2 className="text-2xl font-bold mb-4">Secure Reset</h2>
          <p className="text-white/80 max-w-sm mx-auto">
            Create a strong password to keep your Identra account secure.
          </p>
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
